import React, { Component } from 'react';
import { isEqual } from 'lodash';
import classnames from 'classnames';

import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import MoreButton from 'components/buttons/more-button/MoreButton';
import NotesModal from 'modules/notes/components/modal/NotesModal';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import { t } from 'utils/language';
import { getTruncatedDisplayDateAndTime } from 'utils/date';
import { getEntityFromProfile, getFullName } from 'utils/view';
import { doesMatchSearchQuery } from 'utils/influencers';
import { expandDataProps } from 'utils/helper/common';
import { insertIf } from 'utils/list';

import type { Data, MessengerConversation, MessengerMessage, SubcampaignInfluencerProfile } from 'types';

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

type Props = {
  searchQuery: string | undefined | null,
  profile: SubcampaignInfluencerProfile,
  lastMessage: MessengerMessage,
  hasNotification: boolean,
  selected: boolean,
  lightweightSelected: boolean,
  conversation: MessengerConversation,
  onSelectConversation: (a: MessengerConversation) => void,
  onMarkConversationAsUnread: (a: MessengerConversation) => void,
  data?: Data,
  forwardedRef?: (node: HTMLLIElement) => void,
  isFetching?: boolean
};


class MessengerContact extends Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    const {
      profile,
      searchQuery,
      lastMessage,
      hasNotification,
      selected,
      lightweightSelected,
      isFetching
    } = this.props;

    return !isEqual(profile, nextProps.profile) ||
      !isEqual(lastMessage, nextProps.lastMessage) ||
      !isEqual(searchQuery, nextProps.searchQuery) ||
      hasNotification !== nextProps.hasNotification ||
      selected !== nextProps.selected ||
      lightweightSelected !== nextProps.lightweightSelected ||
      isFetching !== nextProps.isFetching;
  }

  handleClick = () => {
    const { onSelectConversation, conversation } = this.props;

    onSelectConversation(conversation);
  };

  handleMarkAsUnread = () => {
    const { onMarkConversationAsUnread, conversation } = this.props;

    onMarkConversationAsUnread(conversation);
  };

  renderContextMenu() {
    const { profile, hasNotification } = this.props;

    const entity = getEntityFromProfile(profile);

    const items = [
      ...insertIf(!hasNotification, {
        name: 'mark-as-unread',
        text: t('messenger.context_menu.mark_as_unread_label'),
        onClick: this.handleMarkAsUnread
      }),
      ...insertIf(!hasNotification, {
        type: 'separator'
      }),
      {
        type: 'modal',
        name: 'notes',
        text: t('context_menu.notes_label'),
        meta: {
          influencer: profile
        },
        extra: {
          component: NotesModal
        }
      },
      {
        type: 'lists',
        name: 'lists',
        text: t('context_menu.lists_label'),
        meta: {
          influencer: profile
        }
      },
      {
        name: 'profile',
        text: t('context_menu.profile_label'),
        link: entity?.url,
        openInNewTab: true
      }
    ];

    return <MoreButtonWithMenu width={20} contextMenuProps={{ items }} />;
  }

  render() {
    const {
      profile,
      searchQuery,
      lastMessage,
      selected,
      lightweightSelected,
      hasNotification,
      data,
      forwardedRef
    } = this.props;

    const isFilteredOut = searchQuery && profile && !doesMatchSearchQuery(searchQuery, profile);

    const classes = classnames({
      'messenger-contact': true,
      'messenger-contact--selected': selected,
      'messenger-contact--notification': hasNotification,
      'messenger-contact--lightweight': profile.isLightweight || profile.approvalStatus !== 'approved',
      'messenger-contact--lightweight-selected': lightweightSelected,
      'messenger-contact--filtered-out': isFilteredOut
    });

    const entity = getEntityFromProfile(profile);

    return (
      <li onClick={this.handleClick} className={classes} ref={forwardedRef}>
        <div className="messenger-contact__content">
          <div className="messenger-contact__avatar">
            <EntityAvatar
              bordered
              size="big"
              entity={entity}
              link={entity?.url}
              openInNewTab
            />
          </div>
          <div className="messenger-contact__summary">
            <strong
              className="messenger-contact__artist-name text-overflow-ellipsis"
              data-hj-suppress
              {...expandDataProps(data)}
            >
              {entity?.displayName}
            </strong>
            <span className="messenger-contact__full-name text-overflow-ellipsis" data-hj-suppress>
              {getFullName(entity)}
            </span>
          </div>
        </div>
        <div className="messenger-contact__menu-button">
          {this.renderContextMenu()}
        </div>
        {
          lastMessage && !lastMessage.isEmpty &&
            <time className="messenger-contact__time">
              {getTruncatedDisplayDateAndTime(lastMessage.createdAt)}
            </time>
        }
      </li>
    );
  }
}

export default MessengerContact;
